import { inject, Injectable } from '@angular/core';
import { DialogService } from 'primeng/dynamicdialog';
import { ConfirmDialogComponent } from '../components/confirmation-dialog.component';
import { BranchStateService } from '../services/branch-state.service';
import { LanguagesService } from '../services/languages.service';

@Injectable({ providedIn: 'root' })
export class CustomCodeUtilService {
    private dialogService = inject(DialogService);
    private languagesService = inject(LanguagesService);
    private branchStateService = inject(BranchStateService);

    async appendCustomCode(srcURL: string) {
        if (window.__is_custom_code_loaded) {
            return;
        }
        const reqId = window.crypto.randomUUID();
        this.dispatchHttpRequestLoaderEvent(reqId);

        await new Promise<void>((resolve, reject) => {
            const script = document.createElement('script');
            script.type = 'text/javascript';
            // Use BranchStateService instead of window.location.search
            // because the browser URL may not have updated yet after redirect
            const branch = this.branchStateService.getBranch();
            if (branch) {
                srcURL += (srcURL.includes('?') ? '&' : '?') + `branch=${encodeURIComponent(branch)}`;
            }
            script.src = srcURL;

            script.onload = () => {
                window.__is_custom_code_loaded = true;
                this.dispatchHttpResponseLoaderEvent(reqId);
                resolve();
            };

            script.onerror = () => {
                window.__is_custom_code_failed = true;
                this.dispatchCustomCodeFailedEvent();
                this.dispatchHttpResponseResetEvent();
                this.showConfirmReloadDialog();
                reject(new Error('Custom code failed to load'));
            };

            document.head.appendChild(script);
        });
    }

    private dispatchCustomCodeFailedEvent() {
        window.dispatchEvent(new CustomEvent('alpha-custom-code-failed'));
    }

    private dispatchHttpRequestLoaderEvent(reqId: string) {
        window.dispatchEvent(
            new CustomEvent('http-plugin:httprequest', {
                detail: {
                    reqId,
                },
            }),
        );
    }

    private dispatchHttpResponseLoaderEvent(reqId: string) {
        window.dispatchEvent(
            new CustomEvent('http-plugin:httpresponse', {
                detail: {
                    reqId,
                },
            }),
        );
    }

    private dispatchHttpResponseResetEvent() {
        window.dispatchEvent(new CustomEvent('http-plugin:httpreset'));
    }

    showConfirmReloadDialog(styleClass?: string) {
        this.dialogService
            .open(ConfirmDialogComponent, {
                header:
                    this.languagesService.translateConfig('{{lang.default-cm-initializationFailedMessage}}') ||
                    'Initialization Failed: Please Reload the Application',
                data: {
                    buttonText: this.languagesService.translateConfig('{{lang.default-cm-reload}}') || 'Reload',
                    styleClass,
                },
                width: '35rem',
                closable: false,
            })
            .onClose.subscribe((_) => {
                window.location.reload();
            });
    }

    checkReadyAndExecuteHooks(callback: () => unknown) {
        if (!window.__is_custom_code_loaded) {
            console.error(new Error('Custom code has not loaded.'));
            return;
        }
        return callback();
    }
}
